import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { Seo, faqLd } from '../lib/seo';
import { brands, getWeeklySelection, products, slugifyBrand } from '../data/products';

const homeFaqs = [
  {
    question: '¿Quién puede comprar en VapSilver?',
    answer:
      'La venta está restringida exclusivamente a personas mayores de 18 años. La edad se verifica al entrar en la web y, de forma documental, en el momento de la entrega del pedido.',
  },
  {
    question: '¿Los productos cumplen la normativa española?',
    answer:
      'Sí. Todos los productos están conformes con la Ley 28/2005, el RD 579/2017 y la Directiva 2014/40/UE: envases de líquido con nicotina limitados a 10 ml, depósitos de 2 ml, nicotina máxima de 20 mg/ml y notificación previa a la AEMPS.',
  },
  {
    question: '¿Qué información incluye cada ficha de producto?',
    answer:
      'Cada producto detalla concentración de nicotina, listado completo de ingredientes, instrucciones de uso, posibles efectos adversos, advertencia sanitaria, país de origen y trazabilidad de lote.',
  },
  {
    question: '¿Cómo se realizan los envíos?',
    answer:
      'El plazo habitual es de 7 a 10 días desde que se procesa el pedido, con verificación de edad en la entrega. Si la referencia que has elegido no está en nuestro almacén y hay que pedirla al proveedor, el plazo se amplía a entre 15 y 25 días; en la ficha de cada producto lo indica un asterisco. Baleares, Canarias, Ceuta y Melilla pueden tener restricciones específicas indicadas en el checkout.',
  },
  {
    question: '¿Puedo devolver un producto?',
    answer:
      'Por motivos de seguridad e higiene los líquidos abiertos no son retornables. Los dispositivos sin abrir admiten devolución en 14 días naturales.',
  },
];

export default function Home() {
  const featured = getWeeklySelection();
  const featuredBrands = brands
    .map((brand) => ({
      ...brand,
      total: products.filter((product) => slugifyBrand(product.brand) === brand.slug).length,
    }))
    .filter((brand) => brand.total > 0)
    .sort((a, b) => Number(Boolean(b.image)) - Number(Boolean(a.image)) || b.total - a.total)
    .slice(0, 10);
  const desktopBrandCols =
    featuredBrands.length % 5 === 0 ? 5
    : featuredBrands.length % 4 === 0 ? 4
    : featuredBrands.length % 3 === 0 ? 3
    : featuredBrands.length % 2 === 0 ? 2
    : 4;
  const desktopBrandColsClass =
    desktopBrandCols === 5 ? 'lg:grid-cols-5'
    : desktopBrandCols === 4 ? 'lg:grid-cols-4'
    : desktopBrandCols === 3 ? 'lg:grid-cols-3'
    : 'lg:grid-cols-2';
  const brandGridMaxClass =
    desktopBrandCols === 5 ? 'max-w-[1240px]'
    : desktopBrandCols === 4 ? 'max-w-[1040px]'
    : desktopBrandCols === 3 ? 'max-w-[920px]'
    : 'max-w-[760px]';

  return (
    <>
      <Seo
        title="VapSilver — Cigarrillos electrónicos y líquidos de vapeo en España"
        description="Tienda online española de cigarrillos electrónicos, pods recargables, líquidos y desechables. Información sanitaria detallada en cada producto. Conforme a la Ley 28/2005 y RD 579/2017."
        path="/"
        keywords={['vapeo España', 'cigarrillo electrónico', 'pod recargable', 'líquido vapeo', 'tienda vapeo online']}
        jsonLd={faqLd(homeFaqs)}
      />

      {/* HERO
          Sin orbes propios: el fondo lo aporta BackgroundFx para mantener
          una apariencia uniforme entre el hero y las siguientes secciones. */}
      <section className="relative">
        <div className="container-app relative pt-14 pb-1 sm:pt-24 sm:pb-20 lg:pt-28 lg:pb-3 text-center">
          <h1 className="text-[2.75rem] leading-[1] sm:text-6xl md:text-7xl lg:text-8xl sm:leading-[0.95] font-semibold tracking-tightest mb-5 sm:mb-6 animate-fade-up">
            Vapeo<span className="text-vapor-400">.</span>
            <br />
            <span className="text-bone-300">Sin humo<span className="text-vapor-400">.</span> Sin ruido.</span>
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-lg text-bone-300 leading-relaxed mb-10 animate-fade-up">
            Descubre una selección premium de dispositivos, líquidos y pods desechables diseñados para ofrecer el máximo rendimiento, calidad y confianza. Información clara en cada producto para una experiencia de vapeo superior.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up">
            <Link to="/tienda" className="btn-primary !px-7 !py-4">
              Explorar catálogo
            </Link>
            <Link to="/contacto" className="btn-ghost !px-7 !py-4">
              ¿Alguna duda? Contáctanos
            </Link>
          </div>
        </div>
      </section>

      {/* MARCAS */}
      <section className="container-app py-10 sm:py-20" aria-labelledby="cat-h">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <h2 id="cat-h" className="text-3xl sm:text-4xl font-semibold tracking-tighter">
              Marcas con las que trabajamos
            </h2>
          </div>
          <Link to="/tienda" className="hidden sm:inline text-sm text-bone-300 hover:text-vapor-400 underline-offset-4 underline">
            Ver catálogo completo →
          </Link>
        </div>
        <div className={`mx-auto w-full ${brandGridMaxClass} grid grid-cols-2 sm:grid-cols-3 ${desktopBrandColsClass} gap-4`}>
          {featuredBrands.map((brand) => (
            <Link
              key={brand.slug}
              to={`/tienda/marca/${brand.slug}`}
              className="group mx-auto w-full max-w-[18rem] overflow-hidden rounded-[1.5rem] border border-white/10 bg-white hover:border-vapor-400/70 hover:shadow-glow-sm transition-all"
            >
              {/* Mismo fondo blanco que en la tienda, por los logos de tinta oscura,
                  salvo las marcas que traen su propio color en `cardBackground`. */}
              <div
                className={`aspect-square relative flex items-center justify-center overflow-hidden p-5 ${
                  brand.cardBackground ? '' : 'bg-white'
                }`}
                style={brand.cardBackground ? { background: brand.cardBackground } : undefined}
              >
                {brand.image ? (
                  <img
                    src={brand.image}
                    alt={brand.label}
                    className="relative z-10 max-h-[100%] max-w-[100%] object-contain transition-transform group-hover:scale-105"
                  />
                ) : (
                  <span className="relative z-10 text-5xl font-semibold text-ink-500">{brand.label.charAt(0)}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DESTACADOS */}
      <section className="container-app py-10 sm:py-20" aria-labelledby="feat-h">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <h2 id="feat-h" className="text-3xl sm:text-4xl font-semibold tracking-tighter">
              Selección esta semana
            </h2>
          </div>
        </div>
        {/* Tres columnas y no cuatro: la selección son nueve artículos y así cierra
            en tres filas completas, sin dejar una tarjeta huérfana al final. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* PROMESAS */}
      <section className="container-app py-10 sm:py-20" aria-labelledby="why-h">
        <h2 id="why-h" className="sr-only">Garantías VapSilver</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { t: 'Trazabilidad de lote', d: 'Cada producto incluye lote y consumo preferente para auditoría completa.' },
            { t: 'Verificación +18 doble', d: 'Confirmación al entrar y verificación documental en la entrega.' },
            { t: 'Notificado en AEMPS', d: 'Líquidos y dispositivos registrados antes de su comercialización.' },
            { t: 'Reciclaje RAEE', d: 'Recogemos tus dispositivos al final de su vida útil para reciclaje.' },
          ].map((f) => (
            <div key={f.t} className="card p-6">
              <h3 className="font-medium text-bone-50 mb-2">{f.t}</h3>
              <p className="text-sm text-bone-300 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-app py-10 sm:py-20" aria-labelledby="faq-h">
        <h2 id="faq-h" className="text-3xl sm:text-4xl font-semibold tracking-tighter mb-10">
          Lo que más nos preguntáis
        </h2>
        <div className="columns-1 lg:columns-2 gap-4 [column-fill:_balance]">
          {homeFaqs.map((f) => (
            <details key={f.question} className="card p-5 group mb-4 break-inside-avoid">
              <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                <span className="font-medium text-bone-50">{f.question}</span>
                <span className="text-vapor-400 transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="text-sm text-bone-300 leading-relaxed mt-3">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
